import {
  useElements,
  CardElement,
  CardNumberElement,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { isValidTaxExemptionNumber } from "widgets/booker/contexts/CartContextProvider/helpers";
import { getResolvedProductSellingConfig } from "shared/utils";
import { EnumProductType } from "gql/generated";
import type { StripeCardFormChange } from "widgets/booker/shared/StripeSplitCardFields";

export type PaymentType =
  | "card"
  | "direct_debit"
  | "bank_transfer"
  | "offline_payment";
export type PaymentMethodLabels = Partial<Record<PaymentType, string | null>>;
const PAD_AMOUNT_LIMIT_CENTS = 300000;

export const usePaymentMethod = () => {
  const {
    updateStripeCardElement,
    stripeCardElement,
    updatePaymentMethodId,
    updateCardPostalCode,
    grandTotal,
    paymentMethodId,
    selectedTierId,
    taxExempt,
    taxExemptionNumber,
    updateTaxExemption,
    customer,
    customerAddress,
  } = useCartContext();
  const { product, booker, paymentRequestGrandTotal } = useBookerContext();
  const elements = useElements();
  const stripe = useStripe();

  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );
  const stripeRegion = booker?.paymentMetadata?.stripeRegion ?? "CA";
  /**
   * Per-booker Payment Settings switch. When on, card entry uses the split
   * card form (number / expiry / CVC / postal) with the postal code optional
   * instead of Stripe's combined CardElement, which always requires it.
   */
  const optionalCardPostalCode =
    !!booker?.paymentMetadata?.optionalCardPostalCode;

  const resolvedPaymentConfig = useMemo(() => {
    const pms = (booker as any)?.paymentMethodSet;
    if (pms && pms.acceptedPaymentMethods?.length) {
      return {
        acceptedMethods: pms.acceptedPaymentMethods.filter(Boolean) as string[],
        bankTransferInstructions: pms.bankTransferInstructions ?? "",
        checkoutDescription: pms.checkoutDescription ?? "",
        methodLabels: (pms.methodLabels ?? {}) as PaymentMethodLabels,
      };
    }
    return {
      acceptedMethods:
        (booker?.paymentMetadata as any)?.acceptedPaymentMethods ?? ["card"],
      bankTransferInstructions:
        (booker?.paymentMetadata as any)?.bankTransferInstructions ?? "",
      checkoutDescription: "",
      methodLabels: {} as PaymentMethodLabels,
    };
  }, [booker]);

  const acceptedMethods = resolvedPaymentConfig.acceptedMethods;
  const acceptCard = acceptedMethods.includes("card");
  const acceptDirectDebit = acceptedMethods.includes("direct_debit");
  const acceptBankTransfer = acceptedMethods.includes("bank_transfer");
  const acceptOfflinePayment = acceptedMethods.includes("offline_payment");
  const checkoutDescription = resolvedPaymentConfig.checkoutDescription;
  const { methodLabels } = resolvedPaymentConfig;

  const firstEnabledMethod = (acceptedMethods[0] ?? "card") as PaymentType;

  const renewableAmountDue =
    product?.type === EnumProductType.RenewableFee
      ? (selectedTierId
          ? sellingConfig.options.find(
              (o: { _id?: string }) => o._id === selectedTierId,
            )?.price
          : undefined) ??
        sellingConfig.basePrice ??
        0
      : 0;

  // A blank invoice's real amount due lives in its line items
  // (paymentRequestGrandTotal), not on the hidden $0 placeholder product's
  // cart total.
  const amountDueCents =
    paymentRequestGrandTotal ??
    (product?.type === EnumProductType.RenewableFee
      ? renewableAmountDue
      : grandTotal);

  const [selectedPaymentType, setSelectedPaymentType] =
    useState<PaymentType>(acceptCard ? "card" : firstEnabledMethod);

  const isPadOverLimit =
    stripeRegion === "CA" && amountDueCents > PAD_AMOUNT_LIMIT_CENTS;

  useEffect(() => {
    if (!acceptCard && firstEnabledMethod !== "card") {
      updatePaymentMethodId(firstEnabledMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedPaymentType === "direct_debit" &&
      stripeRegion === "CA" &&
      isPadOverLimit
    ) {
      const fallback = acceptCard
        ? "card"
        : acceptBankTransfer
          ? "bank_transfer"
          : acceptOfflinePayment
            ? "offline_payment"
            : firstEnabledMethod;
      setSelectedPaymentType(fallback);
      updatePaymentMethodId(fallback === "card" ? "" : fallback);
    }
  }, [isPadOverLimit, selectedPaymentType, stripeRegion, updatePaymentMethodId, acceptCard, acceptBankTransfer, acceptOfflinePayment, firstEnabledMethod]);

  const handlePaymentTypeChange = (type: PaymentType) => {
    setSelectedPaymentType(type);
    if (type === "direct_debit") {
      if (stripeRegion === "CA" && isPadOverLimit) {
        return;
      }
      updateStripeCardElement(undefined);
      updateCardPostalCode("");
      updatePaymentMethodId("direct_debit");
    } else if (type === "bank_transfer" || type === "offline_payment") {
      updateStripeCardElement(undefined);
      updateCardPostalCode("");
      updatePaymentMethodId(type);
    } else {
      updatePaymentMethodId("");
    }
  };

  /** Combined Stripe CardElement (postal code collected inside the iframe). */
  const handleCardElementChange = async (e: StripeCardElementChangeEvent) => {
    if (e.complete) {
      if (elements && stripe) {
        const cardElement = elements.getElement(CardElement) ?? undefined;
        updateStripeCardElement(cardElement);

        if (cardElement) {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });
          if (paymentMethod && !error) updatePaymentMethodId(paymentMethod.id);
        }
      }
    } else {
      updateStripeCardElement(undefined);
      updatePaymentMethodId("");
    }
  };

  /**
   * Split card form (used when `optionalCardPostalCode` is on). The postal
   * code lives outside Stripe's iframe, so we pass it along explicitly via
   * billing_details when minting the PaymentMethod.
   */
  const handleSplitCardChange = async (payload: StripeCardFormChange) => {
    updateCardPostalCode(payload.postalCode);
    if (!payload.complete || !payload.cardElement) {
      updateStripeCardElement(undefined);
      updatePaymentMethodId("");
      return;
    }

    updateStripeCardElement(payload.cardElement);

    if (elements && stripe) {
      const cardElement =
        elements.getElement(CardNumberElement) ?? payload.cardElement;
      const postalCode = payload.postalCode.trim();
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${customer.firstName} ${customer.lastName}`.trim() || undefined,
          email: customer.email || undefined,
          phone: customer.phone || undefined,
          ...(postalCode ? { address: { postal_code: postalCode } } : {}),
        },
      });
      if (paymentMethod && !error) updatePaymentMethodId(paymentMethod.id);
    }
  };

  const offerTaxExemption = !!(booker?.paymentMetadata as any)
    ?.offerTaxExemption;

  const isTaxExemptionNumberValid = isValidTaxExemptionNumber(
    taxExemptionNumber,
  );

  const handleTaxExemptToggle = (checked: boolean) => {
    updateTaxExemption(checked, checked ? taxExemptionNumber : "");
  };

  const handleTaxExemptionNumberChange = (value: string) => {
    updateTaxExemption(taxExempt, value);
  };

  const isComplete =
    (selectedPaymentType === "direct_debit"
      ? paymentMethodId === "direct_debit" &&
        !(stripeRegion === "CA" && isPadOverLimit)
      : selectedPaymentType === "bank_transfer" ||
          selectedPaymentType === "offline_payment"
        ? paymentMethodId === selectedPaymentType
        : !!stripeCardElement) &&
    (!taxExempt || isTaxExemptionNumberValid);

  const hasAmountDue =
    paymentRequestGrandTotal != null
      ? paymentRequestGrandTotal > 0
      : grandTotal > 0 ||
        (product?.type === EnumProductType.RenewableFee && renewableAmountDue > 0);

  return {
    onChange: handleCardElementChange,
    onSplitCardChange: handleSplitCardChange,
    optionalCardPostalCode,
    defaultPostalCode: customerAddress?.postalCode ?? "",
    isComplete,
    isDisabled: !hasAmountDue,
    acceptCard,
    acceptDirectDebit,
    acceptBankTransfer,
    acceptOfflinePayment,
    methodLabels,
    isPadOverLimit,
    selectedPaymentType,
    onPaymentTypeChange: handlePaymentTypeChange,
    stripeRegion: stripeRegion as "CA" | "US",
    checkoutDescription,
    offerTaxExemption,
    taxExempt,
    taxExemptionNumber,
    isTaxExemptionNumberValid,
    onTaxExemptToggle: handleTaxExemptToggle,
    onTaxExemptionNumberChange: handleTaxExemptionNumberChange,
  };
};
